import axios from 'axios';
import { refreshClient } from './axios.interceptor';
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from '@/config/api.config';
import authStorage from './auth-storage.service';

class AuthService {
  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login response
   */
  async login(email, password) {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`,
      { email, password },
      { __skipRefresh: true },
    );

    if (response.data.access_token) {
      this.setAuthData(response.data);
    }

    return response.data;
  }

  async logout() {
    try {
      // Une déconnexion ne se rejoue pas : si le serveur répond 401, c'est
      // que la session est déjà close. Sans ce drapeau, l'appel repartait
      // dans la file du rafraîchissement et n'en revenait pas.
      await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGOUT}`,
        undefined,
        { __skipRefresh: true },
      );
    } finally {
      this.clearAuthData();
    }
  }

  /**
   * @returns {Promise} Refresh response
   */
  async refreshToken() {
    const refreshToken = authStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await refreshClient.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      },
    );

    if (response.data.access_token) {
      this.setAuthData(response.data);
    }

    return response.data;
  }

  /**
   * Store authentication data in the store the "remember me" choice points at
   * @param {Object} data - Auth data (access_token, refresh_token, user)
   */
  setAuthData(data) {
    if (data.access_token) {
      authStorage.setItem(STORAGE_KEYS.JWT, data.access_token);
    }

    if (data.refresh_token) {
      authStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
    }

    if (data.user) {
      authStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    }
  }

  /**
   * Clear all authentication data from both stores
   */
  clearAuthData() {
    authStorage.clearSession();
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!authStorage.getItem(STORAGE_KEYS.JWT);
  }

  /**
   * Get current user from storage
   * @returns {Object|null}
   */
  getCurrentUser() {
    const userStr = authStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Get JWT token
   * @returns {string|null}
   */
  getToken() {
    return authStorage.getItem(STORAGE_KEYS.JWT);
  }
  async requestPasswordReset(email) {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.RESET_PASSWORD_REQUEST}`,
      { email },
      { __skipRefresh: true },
    );
    return response.data;
  }
  async resetPassword(verificationCode, password) {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`,
      { verificationCode, password },
      { __skipRefresh: true },
    );
    return response.data;
  }
  /**
   * Choisit un nouveau mot de passe depuis un compte deja connecte. C'est la
   * seule route qu'un mot de passe temporaire ouvre.
   */
  async changePassword(currentPassword, password) {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.CHANGE_PASSWORD}`,
      { currentPassword, password },
    );
    return response.data;
  }
  async validateCode(verificationCode) {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.VERIFY_CODE}`,
      { verificationCode },
      { __skipRefresh: true },
    );
    return response.data; // should include something like { valid: true } or throw 401/400 if invalid
  }
}

export default new AuthService();
